// EnneagramChart — a single petal: purely presentational, no geometry math
// (that lives in geometry.ts). Hover/selected/dim states are plain CSS classes
// so all motion is CSS transitions (Mali-safe: opacity/transform/box-shadow-ish
// SVG filter kept subtle, never blur/backdrop-filter).

import React, { useState } from "react";

export interface PetalProps {
  index: number;
  path: string;
  color: string;
  /** id of this petal's <linearGradient> (defined once, up in EnneagramChart's
   *  <defs>) — referenced here via `fill="url(#...)"` for the base/rest paint. */
  gradientId: string;
  label?: string;
  value: number;
  showValue?: boolean;
  selected: boolean;
  dimmed: boolean;
  onSelect?: (index: number) => void;
  labelPoint: { x: number; y: number };
}

export function Petal({ index, path, color, gradientId, label, value, showValue, selected, dimmed, onSelect, labelPoint }: PetalProps) {
  const [hovered, setHovered] = useState(false);

  const classes = [
    "enn-petal",
    hovered && "enn-petal--hover",
    selected && "enn-petal--selected",
    dimmed && "enn-petal--dimmed",
  ].filter(Boolean).join(" ");

  const handleKeyDown = (e: React.KeyboardEvent<SVGGElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect?.(index);
    }
  };

  return (
    <g
      className={classes}
      style={{ "--enn-petal-color": color } as React.CSSProperties}
      role="button"
      tabIndex={0}
      aria-label={label ? `${label} — ${Math.round(value)} %` : `Typ ${index + 1} — ${Math.round(value)} %`}
      aria-pressed={selected}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setHovered(true)}
      onBlur={() => setHovered(false)}
      onClick={() => onSelect?.(index)}
      onKeyDown={handleKeyDown}
    >
      {/* soft token-coloured halo instead of a filter/blur drop-shadow (Mali-safe) —
          fades in on hover/selected via CSS opacity only. */}
      <circle className="enn-petal__halo" cx={labelPoint.x} cy={labelPoint.y} r={16} />
      {/* "Tiny shadow underneath" (art-direction #7), faked with a solid,
          low-opacity duplicate of the exact same outline, nudged a couple of
          viewBox units in one fixed screen-space direction — NOT a blur
          filter (Mali-unsafe), just a flat offset shape peeking out from
          under the petal, like Nothing OS's flat depth cues. The offset is
          screen-space (not rotated per petal) on purpose: the whole flower
          reads as lit from one consistent direction, same as Apple Health. */}
      <path className="enn-petal__shadow" d={path} transform="translate(0 1.6)" />
      {/* Base fill comes from the SVG `fill` attribute (not a CSS class rule)
          so hover/selected can still override it via ordinary stylesheet
          specificity (a stylesheet rule always beats a presentation
          attribute) — see .enn-petal__shape in enneagram.css. */}
      <path className="enn-petal__shape" d={path} fill={`url(#${gradientId})`} />
      {showValue && (
        <text
          className="enn-petal__value"
          x={labelPoint.x}
          y={labelPoint.y}
          textAnchor="middle"
          dominantBaseline="middle"
        >
          {Math.round(value)}%
        </text>
      )}
    </g>
  );
}
